import { EMITTER_TYPES, getEmitterTypeToEnum } from '@semios/app-platform-values-helpers-and-types'

type PointsData = {
  [key: string]: {
    [key: string]: boolean
  }
}

type ValueGroupData = {
  points?: PointsData
  blocks?: Record<string, Record<string, boolean>>
  properties?: Record<string, Record<string, boolean>>
}

type ValueGroupEnums = {
  environment: ValueGroupData
  waterManagement: ValueGroupData
  sprays: ValueGroupData
  pests?: ValueGroupData
  cropDevelopment: ValueGroupData
  equipment?: ValueGroupData
  diseases: ValueGroupData
}

export const emitterList = EMITTER_TYPES.map((emitterType) => getEmitterTypeToEnum(emitterType))

export const valueGroupEnums = (insectIds?: number[], trapInsectIds?: number[]): ValueGroupEnums => {
  return {
    environment: {
      points: {
        dewPoint: {
          dewPoint_in_c: true,
        },
        humidity: {
          humidity_in_pct: true,
        },
        leafWetness: {
          leafWetness_last24HoursSum_wetHours: true,
          leafWetness_next24HoursSum_wetHours: true,
        },
        precipitation: {
          precipitation_todayTotalSum_mm: true,
          precipitation_previous7DaysSum_mm: true,
          precipitation_following7DaysSum_mm: true,
          precipitation_last24HoursSum_mm: true,
          precipitation_next24HoursSum_mm: true,
          precipitation_sinceJan1Sum_mm: true,
          precipitation_sinceApr1Sum_mm: true,
          precipitation_sinceJul1Sum_mm: true,
          precipitation_sinceOct1Sum_mm: true,
        },
        temperature: {
          temperature_in_c: true,
          temperatureInversion_aboveVsIn_c: true,
        },
        wetBulb: {
          wetBulb_in_c: true,
        },
        wind: {
          windSpeed_mps: true,
          windDirection_degrees: true,
        },
      },
    },
    waterManagement: {
      blocks: {
        evapotranspiration: {
          evapotranspirationETc_next14DaysSum_mm: true,
          evapotranspirationETc_next7DaysSum_mm: true,
          evapotranspirationETc_previous7DaysSum_mm: true,
          evapotranspirationETc_previous14DaysSum_mm: true,
          evapotranspirationETc_previous30DaysSum_mm: true,
          evapotranspirationETo_next14DaysSum_mm: true,
          evapotranspirationETo_next7DaysSum_mm: true,
          evapotranspirationETo_previous7DaysSum_mm: true,
          evapotranspirationETo_previous14DaysSum_mm: true,
          evapotranspirationETo_previous30DaysSum_mm: true,
        },
      },
      points: {
        plantStress: { plantStress_yesterday_signalTS5: true },
        fruitGrowth: {
          fruitSize_um: true,
          fruitGrowth_last7DaysSum_um: true,
          fruitGrowth_last14DaysSum_um: true,
          fruitGrowth_last30DaysSum_um: true,
          fruitGrowth_next14DaysSum_um: true,
          fruitGrowthTarget_next14DaysSum_um: true,
        },
        soil: { soilAWC_eachDepth_pct: true },
        irrigationActivity: Object.assign(
          {
            // irrigationIsOn_status: true,
            // irrigationActivity_today_cumulativeFlowMeter_gallons: true,
            // irrigationActivity_prev7Days_cumulativeFlowMeter_gallons: true,
            // irrigationActivity_season_cumulativeFlowMeter_gallons: true,
            // irrigationActivity_YTD_cumulativeFlowMeter_gallons: true,
          },
          ...emitterList.map((emitter) => ({
            [`irrigationIsOn_${emitter}_status`]: true,
            [`irrigationActivity_previous7DaysSum_${emitter}_ms`]: true,
            [`irrigationActivity_previous30DaysSum_${emitter}_ms`]: true,
            [`irrigationActivity_sinceJan1Sum_${emitter}_ms`]: true,
            [`irrigationActivity_sinceApr1Sum_${emitter}_ms`]: true,
            [`irrigationActivity_sinceJul1Sum_${emitter}_ms`]: true,
            [`irrigationActivity_sinceOct1Sum_${emitter}_ms`]: true,
            [`irrigationActivity_todaySoFarSum_${emitter}_ms`]: true,
          })),
        ),
        // evapotranspiration: {
        //   precipitation_following7DaysSum_mm: true,
        //   precipitation_previous7DaysSum_mm: true,
        // },
      },
    },
    sprays: {
      blocks: {
        sprayConditions: { sprayConditions_currentHour: true },
      },
    },
    ...(insectIds &&
      trapInsectIds && {
        pests: {
          properties: {
            trapCatches: Object.assign(
              {},
              ...trapInsectIds.map((id) => ({
                [`insectTrapCatches_todaySum_insectId${id}`]: true,
                [`insectTrapCatches_previous2DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_previous3DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_previous7DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_previous14DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_previous30DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_previous60DaysSum_insectId${id}`]: true,
                [`insectTrapCatches_sinceBiofixSum_insectId${id}`]: true,
                [`insectTrapCatches_sinceJan1Sum_insectId${id}`]: true,
                [`insectTrapCatches_sinceJul1Sum_insectId${id}`]: true,
              })),
            ),
          },
          blocks: {
            pestModels: Object.assign(
              {},
              ...insectIds.map((id) => ({
                [`insectDegreeDays_sinceBiofixSumMinMedianMax_insectId${id}_degreeDaysC`]: true,
                [`insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId${id}_degreeDaysC`]: true,
                [`insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId${id}_degreeDaysC`]: true,
                ...(id === 6 ? { almondHullSplit_date: true } : {}),
              })),
            ),
          },
        },
      }),
    cropDevelopment: {
      blocks: {
        almondBloom: {
          almondBloom_today_pct: true,
          almondBloom_next7Days_pct: true,
          almondBloom_next14Days_pct: true,
        },
        almondHullSplit: {
          almondHullSplit_date: true,
        },
        beeHours: {
          beeHours_last7daysSum_hours: true,
          beeHours_last24hoursSum_hours: true,
          beeHours_last30daysSum_hours: true,
          beeHours_next7daysSum_hours: true,
          beeHours_next14daysSum_hours: true,
        },
        chill: {
          chillHoursBetween32And45_sinceMar1SumMinMedianMax_hours: true,
          chillHoursBetween32And45_sinceMay1SumMinMedianMax_hours: true,
          chillHoursBetween32And45_sinceSep1SumMinMedianMax_hours: true,
          chillHoursBetween32And45_sinceNov1SumMinMedianMax_hours: true,
          chillHoursLessThan45_sinceMar1SumMinMedianMax_hours: true,
          chillHoursLessThan45_sinceMay1SumMinMedianMax_hours: true,
          chillHoursLessThan45_sinceSep1SumMinMedianMax_hours: true,
          chillHoursLessThan45_sinceNov1SumMinMedianMax_hours: true,
          chillPortions_sinceMar1SumMinMedianMax_portions: true,
          chillPortions_sinceMay1SumMinMedianMax_portions: true,
          chillPortions_sinceSep1SumMinMedianMax_portions: true,
          chillPortions_sinceNov1SumMinMedianMax_portions: true,
        },
      },
    },
    // equipment: {
    //   points: {
    //     windMachine: {
    //       windMachine_batteryVoltage_v: true,
    //       windMachine_isRunning: true,
    //       windMachine_rpm: true,
    //       windMachine_status: true,
    //       windMachine_temperature_c: true,
    //     },
    //   },
    // },
    diseases: {
      blocks: {
        alternaria: {
          alternariaCumulativeRolling7Days_yesterday_dsv: true,
          alternariaCumulativeRolling7Days_today_dsv: true,
          alternariaCumulativeRolling7Days_tomorrow_dsv: true,
          alternariaCumulativeRolling7Days_next7DaysMax_dsv: true,
          alternariaCumulativeRolling7Days_next14DaysMax_dsv: true,
        },
        fireBlight: {
          fireBlight_today_trv: true,
          fireBlight_tomorrow_trv: true,
          fireBlight_yesterday_trv: true,
          fireBlight_next7DaysMax_trv: true,
          fireBlight_next14DaysMax_trv: true,
        },
        // grape_powdery_mildew: {
        //   grapePowderyMildewModelPredictedRisk_today: true,
        // },
        // cherry_powdery_mildew: {
        //   cherryPowderyMildewModelPredictedRisk_today: true,
        // },
      },
    },
  }
}
